import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  HomeIcon,
  BookOpenIcon,
  CogIcon,
  CalendarIcon,
  FlagIcon,
  AcademicCapIcon,
  ChevronDoubleLeftIcon,
} from '@heroicons/react/24/outline';

const navigation = [
  { name: 'Dashboard', href: '/app', icon: HomeIcon },
  { name: 'Curricula', href: '/app/curricula', icon: AcademicCapIcon },
  { name: 'Calendar', href: '/app/calendar', icon: CalendarIcon },
  { name: 'Goals', href: '/app/goals', icon: FlagIcon },
  { name: 'Settings', href: '/app/settings', icon: CogIcon },
];

export default function Layout() {
  const { theme, toggleTheme } = useTheme();
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  if (!currentUser) {
    // If not authenticated, don't show sidebar or navigation
    return <Outlet />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Mobile Top Bar */}
      <div className="flex md:hidden items-center justify-between h-16 px-4 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 z-20">
        <button
          onClick={() => setSidebarOpen(true)}
          className="text-gray-700 dark:text-gray-200 focus:outline-none"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <img src="/growault-logo.png" alt="Growault Logo" className="h-8 w-auto" />
        <div className="relative">
          <button onClick={() => setProfileMenuOpen(!profileMenuOpen)} className="flex items-center space-x-2 focus:outline-none">
            <span className="inline-block w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center font-bold">
              {currentUser?.email?.charAt(0).toUpperCase() || 'U'}
            </span>
          </button>
          {profileMenuOpen && (
            <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-800 rounded-md shadow-lg py-2 z-50">
              <button
                onClick={() => {
                  navigate('/app/profile');
                  setProfileMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Profile
              </button>
              <button
                onClick={async () => {
                  await logout();
                  navigate('/login');
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Log out
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Sidebar (mobile overlay) */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          <div className="fixed inset-0 bg-black bg-opacity-30" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white dark:bg-gray-800 shadow-lg">
            <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
              <img src="/growault-logo.png" alt="Growault Logo" className="h-8 w-auto" />
              <button
                onClick={() => setSidebarOpen(false)}
                className="text-gray-700 dark:text-gray-200 focus:outline-none"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
              {/* Main navigation */}
              {navigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  end={item.href === "/app"}
                  className={({ isActive }) =>
                    `flex items-center px-4 py-2 mt-2 text-sm font-medium rounded-lg transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-gray-700 ${isActive ? 'bg-gray-200 dark:bg-gray-700' : ''}`
                  }
                >
                  <item.icon className="w-5 h-5 mr-3" />
                  {item.name}
                </NavLink>
              ))}
            </nav>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={toggleTheme}
                className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
              >
                {theme === 'light' ? '🌙 Dark Mode' : '☀️ Light Mode'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar (desktop) */}
      <div className={`hidden md:fixed md:inset-y-0 md:left-0 md:shadow-lg md:flex md:flex-col transition-all duration-300 ${isSidebarCollapsed ? 'md:w-20' : 'md:w-64'}`}>
        <div className="flex flex-col h-full bg-white dark:bg-gray-800">
          <div className={`flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700 ${isSidebarCollapsed ? 'justify-center' : 'justify-between'}`}>
            {!isSidebarCollapsed && <img src="/growault-logo.png" alt="Growault Logo" className="h-10 w-auto" />}
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none"
            >
              <ChevronDoubleLeftIcon className={`w-6 h-6 transition-transform duration-300 ${isSidebarCollapsed ? 'rotate-180' : ''}`} />
            </button>
          </div>
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {/* Main navigation */}
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                end={item.href === "/app"}
                className={({ isActive }) =>
                  `flex items-center px-2 py-2 text-sm font-medium rounded-lg transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-gray-700 ${isActive ? 'bg-gray-200 dark:bg-gray-700' : ''} ${isSidebarCollapsed ? 'justify-center' : ''}`
                }
                title={isSidebarCollapsed ? item.name : undefined}
              >
                <item.icon className={`w-6 h-6 ${!isSidebarCollapsed ? 'mr-3' : ''}`} />
                {!isSidebarCollapsed && <span className="flex-1">{item.name}</span>}
              </NavLink>
            ))}
          </nav>
          <div className={`p-2 border-t border-gray-200 dark:border-gray-700 ${isSidebarCollapsed ? 'px-2' : 'p-4'}`}>
            <button
              onClick={toggleTheme}
              className={`flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600`}
              title={isSidebarCollapsed ? (theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode') : undefined}
            >
              <span className="text-xl">{theme === 'light' ? '🌙' : '☀️'}</span>
              {!isSidebarCollapsed && <span className="ml-2">{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${isSidebarCollapsed ? 'md:pl-20' : 'md:pl-64'}`}>
        {/* Top Bar with Profile Icon (desktop) */}
        <div className="hidden md:flex justify-end items-center h-16 px-8 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <button
              onClick={() => setProfileMenuOpen(!profileMenuOpen)}
              className="flex items-center space-x-2 focus:outline-none"
            >
              <span className="inline-block w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center font-bold">
                {currentUser?.email?.charAt(0).toUpperCase() || 'U'}
              </span>
              <span className="hidden sm:block text-gray-700 dark:text-gray-200 font-medium">{currentUser?.email}</span>
            </button>
            {profileMenuOpen && (
              <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-800 rounded-md shadow-lg py-2 z-50">
                <button
                  onClick={() => {
                    navigate('/app/profile');
                    setProfileMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Profile
                </button>
                <button
                  onClick={async () => {
                    await logout();
                    navigate('/login');
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
        <main className="p-4 sm:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
} 